'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/features/auth/hooks'
import {
  createPreset,
  deletePreset,
  executePreset,
  listPresets,
  updatePreset,
} from '@/service/preset.api'
import {
  CreatePresetRequest,
  ExecutePresetRequest,
  Preset,
  PresetSummary,
  UpdatePresetRequest,
} from '@/types/generated/preset'
import { ErrorResponse } from '@/types/service'

export const presetKeys = {
  all: ['presets'] as const,
  lists: (userId: string) => [...presetKeys.all, 'list', userId] as const,
}

export const useListPresets = () => {
  const { user } = useAuth()
  return useQuery<PresetSummary[], ErrorResponse>({
    queryKey: presetKeys.lists(user?.userId ?? ''),
    queryFn: async () => {
      const res = await listPresets()
      return res.presets
    },
    enabled: !!user,
  })
}

export const useCreatePreset = (props?: {
  onSuccess?: (data: Preset, variables: CreatePresetRequest) => void
  onError?: (error: ErrorResponse, variables: CreatePresetRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: CreatePresetRequest) => createPreset(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: presetKeys.lists(user?.userId ?? '') })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useUpdatePreset = (props?: {
  onSuccess?: (data: Preset, variables: UpdatePresetRequest) => void
  onError?: (error: ErrorResponse, variables: UpdatePresetRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: UpdatePresetRequest) => updatePreset(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: presetKeys.lists(user?.userId ?? '') })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useDeletePreset = (props?: {
  onSuccess?: (variables: string) => void
  onError?: (error: ErrorResponse, variables: string) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (presetId: string) => deletePreset(presetId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: presetKeys.lists(user?.userId ?? '') })
      props?.onSuccess?.(variables)
    },
    onError: props?.onError,
  })
}

export const useExecutePreset = (props?: {
  onSuccess?: (variables: ExecutePresetRequest) => void
  onError?: (error: ErrorResponse, variables: ExecutePresetRequest) => void
}) => {
  return useMutation({
    mutationFn: (payload: ExecutePresetRequest) => executePreset(payload),
    onSuccess: (_, variables) => {
      props?.onSuccess?.(variables)
    },
    onError: props?.onError,
  })
}
